import React, { useState } from 'react';
import {
  IndustryStandardProgressMetaData,
  WidgetDefinition,
} from '../../../../shared/types/widget';
import { Typography } from '../../../widgets/Typography';
import IndustryStandardsAlert from './IndustryStandardsAlert';
import { PlayIcon } from '../../../../assets/icons/Play';
import { useFilterData } from '../../../../utilities/hooks/useFilterData';
import useFilters from '../../../../utilities/hooks/useFilters';
import { IndustryStandardType } from '../constants';
import { getIndustryStandardFilterLabel } from '../utils';
import { twMerge } from 'tailwind-merge';

interface Props {
  definition: WidgetDefinition;
  content: IndustryStandardProgressMetaData;
}

export default function IndustryStandardProgressBar({
  definition,
  content,
}: Readonly<Props>) {
  const { configData } = useFilterData();
  const { benchmarkType } = useFilters();

  const [showPopover, setShowPopover] = useState(false);

  // Mouse position for popover x coordinate
  const [mouseX, setMouseX] = useState(0);

  const { title } = definition;
  const { target, value } = content?.data || {};

  /**
   * Get progress end value based on current value
   * @return {number} - progress end value which is used to set end of progress bar
   * reference: https://www.figma.com/design/8Jodv6yKqsQzw2AZr9QmdV/Intellect-Insights-Dashboard?node-id=2201-169049&m=dev
   */
  const getProgressEndValue = () => {
    if (value >= 90) {
      return 100;
    }

    if (value < 90 && value >= 30) {
      return value + 10;
    }

    return 35;
  };

  const progressEndValue = getProgressEndValue();

  const progress = Math.abs(value / progressEndValue) * 100;

  const shouldMoveProgressLabelOutside = progress < 3; // If progress is less than 3%, move label outside for better visibility

  const selectedIndustry = configData?.industries.find((industry) => {
    return industry.id === benchmarkType;
  });

  const getIndustryStandardIndexLabel = () => {
    if (selectedIndustry?.meta.type === IndustryStandardType.SECTOR) {
      return `Industry: ${selectedIndustry?.name} (${content?.data?.benchmark}%)`;
    }

    if (selectedIndustry?.meta.type === IndustryStandardType.SIZE) {
      return `Size: ${selectedIndustry?.name} (${content?.data?.benchmark}%)`;
    }

    if (content?.data?.benchmark) {
      return `Industry: Average Industries (${content?.data?.benchmark}%)`;
    }

    return 'Industry Standard';
  };

  const getIndustryStandardIndexPosition = () => {
    const benchmark = content?.data?.benchmark || 0;
    const position = Math.abs(benchmark / progressEndValue) * 100;

    return position;
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e?.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left; // Mouse position relative to the bar
    setMouseX(x);
  };

  return (
    <div className="px-6 pt-2 pb-6">
      <div className="flex flex-row items-center gap-4 pt-12">
        <Typography variant="body-xs" className="shrink-0 text-gray-700">
          Your organisation
        </Typography>
        <div className="relative w-full">
          <div className="bg-taupe-400 relative h-5 w-full rounded-sm" />
          <div className="absolute bottom-0 left-0 h-5 w-6 translate-y-full pt-2">
            <Typography variant="body-xs" className="text-grey-700">
              0%
            </Typography>
          </div>
          <div
            className="border-grey-300 absolute bottom-0 z-10 h-5 w-0.5 border border-dashed bg-transparent pt-2"
            style={{
              left: `${getIndustryStandardIndexPosition()}%`,
            }}
          >
            <div className="absolute top-0 flex w-128 -translate-x-1/2 -translate-y-full flex-col items-center pb-1">
              <Typography
                variant="body-sm"
                weight="medium"
                className="shrink-0"
              >
                {getIndustryStandardIndexLabel()}
              </Typography>
              <PlayIcon className="size-5" />
            </div>
          </div>

          <div className="absolute right-0 bottom-0 h-5 w-6 translate-y-full pt-2">
            <Typography variant="body-xs" className="text-grey-700">
              {progressEndValue}%
            </Typography>
          </div>
          <div
            className={`absolute top-0 left-0 h-5 cursor-pointer rounded-sm ${target?.isAboveBenchmark ? 'bg-green-500' : 'bg-orange-500'}`}
            onMouseEnter={() => setShowPopover(true)}
            onMouseLeave={() => setShowPopover(false)}
            onMouseMove={handleMouseMove}
            style={{
              width: `${progress}%`,
            }}
          >
            {progress > 0 && (
              <div className="relative flex h-full w-full flex-row items-center justify-end">
                <Typography
                  variant="body-xs"
                  className={twMerge([
                    'absolute top-1/2 -translate-y-1/2',
                    shouldMoveProgressLabelOutside
                      ? 'text-grey-700 -right-2 translate-x-full'
                      : 'right-2 text-white',
                  ])}
                >
                  {value}%
                </Typography>
              </div>
            )}
          </div>

          {showPopover && (
            <div
              onMouseEnter={() => setShowPopover(true)}
              onMouseLeave={() => setShowPopover(false)}
              className="absolute -top-0 z-30 -translate-x-1/2 -translate-y-full rounded-xl border border-gray-100 bg-white shadow-lg"
              style={{
                left: `${mouseX}px`,
              }}
            >
              <div className="relative flex flex-col gap-1 p-3">
                <Typography variant="body-xs" weight="semibold">
                  {title}
                </Typography>
                <Typography
                  variant="body-xs"
                  weight="regular"
                  className="text-blue-700"
                >
                  Your organisation: {value}%
                </Typography>
                <Typography
                  variant="body-xs"
                  weight="regular"
                  className="text-grey-700"
                >
                  Industry standard (
                  {getIndustryStandardFilterLabel(configData, benchmarkType)}
                  ):&nbsp;
                  {content?.data?.benchmark}%
                </Typography>
                <IndustryStandardsAlert
                  shortVersion
                  isAboveBenchmark={target?.isAboveBenchmark}
                  aboveBenchmarkLabel={
                    'Your organisation has surpassed the industry standard.'
                  }
                  belowBenchmarkLabel={
                    'Your organisation is below the industry standard.'
                  }
                />
                <div className="absolute bottom-0 left-1/2 h-3 w-3 -translate-x-1/2 translate-y-1/2 rotate-45 bg-white" />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="mt-12">
        <IndustryStandardsAlert
          isAboveBenchmark={target?.isAboveBenchmark}
          aboveBenchmarkLabel={target?.remark}
          belowBenchmarkLabel={target?.remark}
          ctaLabel={target?.link}
        />
      </div>
    </div>
  );
}
